import { Formik, Form, Field } from 'formik';
import { useEffect, useState } from 'react';

import styled from 'styled-components';
import * as Yup from 'yup';
import { useAppInit } from '../../../contexts/appInitContext';
import { useOrderContext } from '../../../contexts/orderContext';
import { useTranslation } from '../../../contexts/translationContext';

import { EASYCOM_ROUTES } from '../../../navigation';
import Spacing from '../../atoms/Spacing';
import Typography from '../../atoms/Typography';
import PrimaryButton from '../../molecules/PrimaryButton';
import TextInputComponent from './FormikInput';
import { useAppNavigate } from '../../../hooks/useAppNavigate';

const StyledForm = styled(Form)`
  width: 100%;
`;

const SignInEmailForm = () => {
  const { getOrder, setDefaultOrder } = useOrderContext();
  const navigate = useAppNavigate();
  const { params, updateDefaultParams } = useAppInit();
  const { email, initOrderKeys } = params;
  const { translate } = useTranslation();
  const [formError, setFormError] = useState(undefined);

  useEffect(() => () => undefined, []);

  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    orderId: Yup.string()
      .min(3, (value) => translate('x_characters_long', { min: value.min }))
      .required(
        `${translate('order_no', null)} ${translate(
          'is_required',
          null,
        ).toLowerCase()}`,
      ),
    email: Yup.string()
      .email(translate('must_be_valid_email', null))
      .required(
        `${translate('email', null)} ${translate('is_required', null)}`,
      ),
  });

  interface Ivalues {
    email: string;
    orderId: string;
  }

  const initialValues: Ivalues = {
    email: email || '',
    orderId: (initOrderKeys && initOrderKeys[0]) || '',
  };

  const onSubmit = async (values: Ivalues) => {
    setLoading(true);
    setFormError(undefined);
    const { orderId } = values;
    const response = await getOrder({
      orderNumbers: [orderId],
      email: values.email,
    });

    if (response.length > 0) {
      setDefaultOrder(response[0].orderNumber);
      updateDefaultParams({ ...params, email: values.email });
      navigate(EASYCOM_ROUTES.ORDER_PAGE);
    }

    if (response?.errors) {
      setFormError(response?.errors[0].message);
    }
    setLoading(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values: Ivalues) => {
        onSubmit(values);
      }}
    >
      <StyledForm>
        <Typography
          align="center"
          translateKey="login_to_see_order"
          variant="h3"
        />
        <Spacing spacing={3} />
        <Field
          placeholder="placeholder_order_id"
          name="orderId"
          type="text"
          translateKey="order_no"
          component={TextInputComponent}
        />
        <Spacing spacing={4} />
        <Field
          placeholder="placeholder_email_field"
          name="email"
          type="text"
          translateKey="email_field"
          component={TextInputComponent}
        />
        <Spacing spacing={4} />
        <PrimaryButton
          disabled={loading ? true : undefined}
          loading={loading ? true : undefined}
          translateKey="sign_in"
          type="submit"
        />
        <Spacing spacing={4} />
        {formError && (
          <Typography variant="input-error">{formError}</Typography>
        )}
      </StyledForm>
    </Formik>
  );
};

export default SignInEmailForm;
